import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Card, Heading, Body } from '@trustpilot/businessapp-patternlibrary';
import { COLORS } from '@trustpilot/businessapp-patternlibrary/lib/constants';
import { ReactComponent as ArrowDown } from '@trustpilot/icons/lib/simple-arrow-down_16.svg';
import { ReactComponent as ArrowUp } from '@trustpilot/icons/lib/simple-arrow-up_16.svg';
import '@trustpilot/icons/lib/styles.css';
import { interpolate, useTranslations } from 'ilenia';
import DateFormat from 'dateformat';

const formattedTodayDate = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return DateFormat(date, 'mmm d, yyyy');
};

export const PlanDetails: React.FC<any> = ({ pricing, businessUnitName, businessModuleId, businessModuleName }) => {
  const [expanded, setPlanDetailsExpanded] = useState(true);
  const onToggleExpansion = () => setPlanDetailsExpanded(!expanded);
  const [translations] = useTranslations();
  const [subscriptionTermDate] = useState(formattedTodayDate());
  const features = [
    translations[`planDetails.features.${businessModuleId}.first`],
    translations[`planDetails.features.${businessModuleId}.second`],
    translations[`planDetails.features.${businessModuleId}.third`],
    translations[`planDetails.features.${businessModuleId}.fourth`],
    translations[`planDetails.features.${businessModuleId}.fifth`],
    translations[`planDetails.features.${businessModuleId}.sixth`],
    translations[`planDetails.features.${businessModuleId}.seventh`],
    translations[`planDetails.features.${businessModuleId}.eight`],
    translations[`planDetails.features.${businessModuleId}.ninth`],
  ];

  if (!Object.keys(pricing).length) return null;
  
  return (
    <StyledPlanContainer>
      <StyledPlanDescription>
        <StyledHeadingText>
          <Heading level="2">
            {businessModuleName}{translations['planDetails.plan']}
            <StyledDomainText>
              {businessUnitName}
            </StyledDomainText>
          </Heading>
        </StyledHeadingText>
        <StyledPlanDetailsExpander expanded={expanded} onClick={onToggleExpansion}>
          <Body level="2" weight="bold">
            {translations['planDetails.details']}
          </Body>
          <StyledArrowIcon>
            {!expanded ? <ArrowDown /> : <ArrowUp />}
          </StyledArrowIcon>
        </StyledPlanDetailsExpander>
        {expanded && (
          <StyledFeaturesList>
            {features.map((item: any) => (
              item && <StyledFeatureEntry key={item}>
                <StyledFeatureLi>
                  <StyledFeatureEntryText>
                    <Body level="2" weight="normal">
                      {item}
                    </Body>
                  </StyledFeatureEntryText>
                </StyledFeatureLi>
              </StyledFeatureEntry>
            ))}
          </StyledFeaturesList>
        )}
        <StyledInformationBlock>
          {pricing.billingCycle === 'ANNUALLY' && (
            <StyledPriceInformationRow marginTop={8}>
              <StyledBilledYearly>
                <Body level="2" weight="normal">
                  {translations['planDetails.billedYearly']}
                </Body>
              </StyledBilledYearly>
              <StyledPriceMonthlyInfoText>
                <StyledAlignedText>
                  <Body level="2" weight="normal">
                    {pricing.monthlyPrice.price.formattedAmount}
                  </Body>
                </StyledAlignedText>
                <StyledCrossIcon>
                  <StylledCross />
                </StyledCrossIcon>
                <StyledAlignedText>
                  <Body level="2" weight="normal">
                    {translations['planDetails.oneYear']}
                  </Body>
                </StyledAlignedText>
              </StyledPriceMonthlyInfoText>
              <StyledPriceCalculationText>
                <Body level="2" weight="normal">
                  {pricing.contractPrice.price.formattedAmount}
                </Body>
              </StyledPriceCalculationText>
            </StyledPriceInformationRow>
          )}
          {pricing.billingCycle === 'MONTHLY' && (
            <StyledPriceInformationRow marginTop={8}>
              <StyledPriceMonthlyInfoText>
                <StyledAlignedText>
                  <Body level="2" weight="normal">
                    {translations['planDetails.billedMontly']}
                  </Body>
                </StyledAlignedText>
              </StyledPriceMonthlyInfoText>
              <StyledPriceCalculationText>
                <Body level="2" weight="normal">
                  {pricing.contractPrice.price.formattedAmount}
                </Body>
              </StyledPriceCalculationText>
            </StyledPriceInformationRow>
          )}

          <StyledDivider />
          <StyledPriceInformationRow>
            <StyledTotalPerSubscriptionInfoText>
              <Body level="2" weight="normal">
                {translations['planDetails.totalPerSubscription']}
              </Body>
            </StyledTotalPerSubscriptionInfoText>
            <StyledTotalPerSubscriptionInfoPrice>
              <Body level="2" weight="normal">
                {pricing.contractPrice.totalPrice.formattedAmount}
              </Body>
            </StyledTotalPerSubscriptionInfoPrice>
          </StyledPriceInformationRow>
          <StyledPriceInformationRow marginTop={8}>
            <StyledDueTodayInfoText>
              <Body level="1" weight="bold">
                {translations['planDetails.dueToday']}
              </Body>
            </StyledDueTodayInfoText>
            <StyledPriceYearlyInfoPrice>
              <Body level="1" weight="bold">
                {pricing.billingCyclePrice.totalPrice.formattedAmount}
              </Body>
            </StyledPriceYearlyInfoPrice>
          </StyledPriceInformationRow>
          <StyledSubscriptionStartInformation>
            { 
              businessModuleId === 'flex' ?
              <>
                <Body level="2" weight="normal">
                  <div dangerouslySetInnerHTML={{ __html: interpolate(translations['planDetails.flexStartInformation1']) as any }} />
                </Body>
                <EmptyDivider />
                <Body level="2" weight="normal">
                  <div dangerouslySetInnerHTML={{ __html: interpolate(translations['planDetails.flexStartInformation2']) as any }} />
                </Body>
              </> :
              <Body level="2" weight="normal">
                <div dangerouslySetInnerHTML={{ __html: interpolate(translations['planDetails.standartStartInformation'], { DATE: subscriptionTermDate }) as any }} />
              </Body>
            }
          </StyledSubscriptionStartInformation>
        </StyledInformationBlock>
      </StyledPlanDescription>
    </StyledPlanContainer>
  );
};

const StylledCross = () => (
  <svg
    width="16"
    height="16"
    vertical-align="middle"
    xmlns="http://www.w3.org/2000/svg"
    className="trustpilot-icon"
  >
    <g id="16x16_Miscellaneous" strokeWidth="1" fillRule="evenodd">
      <g id="ic-cross">
        <path id="bounds" d="M0 0h16v16H0z" />
        <path
          d="M6.429 8L3.325 4.897a1.111 1.111 0 1 1 1.572-1.572L8 6.43l3.103-3.104a1.111 1.111 0 1 1 1.572 1.572L9.57 8l3.104 3.103a1.111 1.111 0 1 1-1.572 1.572L8 9.57l-3.103 3.104a1.111 1.111 0 1 1-1.572-1.572L6.43 8z"
          id="icon"
          fillRule="nonzero"
        />
      </g>
    </g>
  </svg>
);

const StyledHeadingText = styled.span({
  color: 'rgb(27, 27, 33)',
  letterSpacing: '0.2px',
});

const StyledArrowIcon = styled.div({
  marginLeft: 8,
});

const StyledCrossIcon = styled.span({
  marginRight: 4,
  marginLeft: 4,
  color: '#a9a9a9',
  verticalAlign: 'sub',
  display: 'inline-block',
});

const StyledDomainText = styled.span({
  color: '#6F6F87',
});

const StyledAlignedText = styled.span({
  verticalAlign: 'middle',
  display: 'inline-block',
});

const StyledBilledYearly = styled.div({
  color: '#292929',
  fontSize: 14,
  marginTop: 8,
  marginBottom: 8,
});

const StyledPriceMonthlyInfoText = styled.span({
  marginBottom: 5,
  fontSize: 14,
  fontWeight: 500,
  float: 'left',
});

const StyledPriceCalculationText = styled.span({
  float: 'right',
  marginBottom: 5,
  fontWeight: 500,
});

const StyledDueTodayInfoText = styled.span({
  fontWeight: 700,
  fontSize: 16,
  float: 'left',
});

const StyledPriceYearlyInfoPrice = styled.span({
  float: 'right',
  fontSize: 16,
  fontWeight: 700,
});

const StyledTotalPerSubscriptionInfoText = styled.span({
  float: 'left',
});

const StyledTotalPerSubscriptionInfoPrice = styled.span({
  float: 'right',
});

const StyledPriceInformationRow = styled.div((props: { marginTop?: number }) => ({
  fontSize: 14,
  color: COLORS.grays.darkest,
  marginTop: Number.isInteger(props.marginTop) ? props.marginTop : 0,
}));

const StyledDivider = styled.div({
  backgroundColor: COLORS.grays.cardBorder,
  height: 1,
  marginTop: 8,
  marginBottom: '16px !important',
});

const EmptyDivider = styled.div({
  height: 1,
  marginTop: 8,
  marginBottom: '16px !important',
});

const StyledInformationBlock = styled.div({
  display: 'flex',
  flexDirection: 'column',
  fontSize: 14,
  ' > *:not(:last-child)': {
    marginBottom: 8,
  },
});

const StyledFeatureEntry = styled.div({
  color: COLORS.grays.lighter,
  display: 'flex',
  alignItems: 'center',
});

const StyledFeatureEntryText = styled.p({
  margin: 0,
  color: COLORS.grays.darkest,
});

const StyledFeatureLi = styled.li({
  lineHeight: '20px',
});

const StyledFeaturesList: React.FC<any> = styled.ul({
  marginLeft: '13px',
  marginBottom: '16px',
  padding: '8px 4px 4px',
  marginTop: 0,
});

const StyledPlanContainer = styled(Card)({
  padding: '0px !important',
  height: '100%',
});

const StyledPlanDescription = styled.div({
  padding: '16px 24px 16px 24px',
});

const StyledPlanDetailsExpander: React.FC<any> = styled.div(
  {
    fontSize: 14,
    fontWeight: 500,
    color: COLORS.grays.darkest,
    display: 'flex',
    height: 22,
    cursor: 'pointer',
    marginTop: 16,
  },
  (props: any) => (props.expanded
    ? {
      marginBottom: 0,
    }
    : {
      marginBottom: 16,
    }),
);

const StyledSubscriptionStartInformation = styled.div({
  color: '#6f6f87',
  fontSize: 14,
  lineHeight: '20px',
  marginTop: 16,
});
